import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './mypage.scss';
import { AccountNav } from './AccountNav';
import { AccountUser, ProfileData, todayString, visibilityLabel, workStatusLabel, WorkStatus } from './profileTypes';

export default function MyPage() {

  const navigate = useNavigate();
  const [user, setUser] = useState<AccountUser | null>(null);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loadError, setLoadError] = useState('');

  // 로그인하지 않았으면 로그인 화면으로
  useEffect(() => {
    axios.get('/api/auth/me')
      .then(async (res) => {
        setUser(res.data.user);
        try {
          const p = await axios.get('/api/profile/me');
          setProfile(p.data.profile);
        } catch {
          setLoadError('프로필을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
        }
      })
      .catch(() => navigate('/login', { replace: true }));
  }, [navigate]);

  const handleLogout = async () => {
    await axios.post('/api/auth/logout').catch(() => {});
    navigate('/');
  };

  if (!user) return <div className="account-page" />;

  const today = todayString();
  const upcoming = profile
    ? profile.engagements.filter((e) => e.date >= today).sort((a, b) => a.date.localeCompare(b.date)).slice(0, 3)
    : [];
  const pastCount = profile ? profile.engagements.filter((e) => e.date < today).length : 0;
  const statusCount = (status: WorkStatus) => profile?.works.filter((w) => w.status === status).length ?? 0;

  return (
    <div className="account-page">
      <AccountNav onLogout={handleLogout} />

      <main className="wrap acc-main">
        <p className="eyebrow">My page</p>
        <h1>{user.name}님의 <em>클래시커</em></h1>

        <div className="acc-grid">

          {/* ---------------- profile ---------------- */}
          <section className="acc-card">
            <div className="card-head">
              <span>Profile</span>
              <span>내 프로필</span>
            </div>

            {loadError && <p className="status-msg err">{loadError}</p>}

            {!loadError && !profile && (
              <div className="empty">
                <p className="empty-t">아직 프로필이 없습니다.</p>
                <p className="empty-d">악기와 소속, 레퍼토리 몇 곡이면 링크를 보낼 수 있는 프로필이 만들어집니다. 처음엔 10분이면 충분합니다.</p>
                <Link className="btn btn-primary" to="/profile/new">프로필 만들기</Link>
              </div>
            )}

            {profile && (
              <>
                <div className="pf-top">
                  <div>
                    <div className="pf-name">{user.name}</div>
                    <div className="pf-role">
                      {[profile.instrument, profile.ensemble].filter(Boolean).join(' · ')}
                    </div>
                    {profile.tagline && <p className="pf-tagline">{profile.tagline}</p>}
                  </div>
                  <span className="badge">{visibilityLabel[profile.visibility]}</span>
                </div>

                <div className="pf-url">classicker.com/{profile.handle}</div>

                <dl className="stats">
                  <div>
                    <dt>레퍼토리</dt>
                    <dd>{profile.works.length}<small>곡</small></dd>
                    <span>연주 {statusCount('played')} · 준비 {statusCount('ready')} · 학습 {statusCount('study')}</span>
                  </div>
                  <div>
                    <dt>연주 이력</dt>
                    <dd>{pastCount}<small>회</small></dd>
                    <span>지난 무대</span>
                  </div>
                  <div>
                    <dt>이번 시즌</dt>
                    <dd>{profile.engagements.length - pastCount}<small>건</small></dd>
                    <span>다가오는 무대</span>
                  </div>
                  <div>
                    <dt>녹음 · 영상</dt>
                    <dd>{profile.recordings.length}<small>개</small></dd>
                    <span>외부 링크</span>
                  </div>
                </dl>

                <div className="pf-sec">
                  <div className="pf-lbl">다가오는 무대</div>
                  {upcoming.length === 0 ? (
                    <p className="pf-none">예정된 무대가 없습니다. 무대가 잡히면 추가해 두세요.</p>
                  ) : upcoming.map((e) => (
                    <div className="pf-line" key={`${e.date}-${e.title}`}>
                      <em>{e.date.slice(5).replace('-', '.')}</em>
                      <span>{e.title} · {e.venue}</span>
                    </div>
                  ))}
                </div>

                <div className="pf-sec">
                  <div className="pf-lbl">레퍼토리</div>
                  {profile.works.length === 0 ? (
                    <p className="pf-none">아직 넣은 곡이 없습니다. 열 곡부터 링크가 쓸 만해집니다.</p>
                  ) : profile.works.slice(0, 4).map((w, i) => (
                    <div className="pf-line" key={i}>
                      <em>{w.catalog || '—'}</em>
                      <span>{w.composer} — {w.title}</span>
                      <b className={w.status === 'played' ? 'on' : ''}>{workStatusLabel[w.status]}</b>
                    </div>
                  ))}
                </div>

                <div className="acc-actions">
                  <Link className="btn btn-ghost" to={`/${profile.handle}`}>내 프로필 보기</Link>
                  <Link className="btn btn-primary" to="/profile/edit">프로필 수정</Link>
                </div>
              </>
            )}
          </section>

          {/* ---------------- account ---------------- */}
          <aside className="acc-card">
            <div className="card-head">
              <span>Account</span>
              <span>계정 정보</span>
            </div>
            <dl className="info">
              <div><dt>이름</dt><dd>{user.name}</dd></div>
              <div><dt>이메일</dt><dd>{user.email || '—'}</dd></div>
              <div><dt>주 악기</dt><dd>{user.instrument || '선택 안 함'}</dd></div>
              <div><dt>로그인</dt><dd>{user.kakao ? '카카오 연결됨' : '이메일'}</dd></div>
            </dl>
            <button className="btn btn-ghost btn-block" type="button" onClick={handleLogout}>로그아웃</button>
          </aside>

        </div>
      </main>
    </div>
  );
}
